import { useState } from 'react';
import { Trans } from 'react-i18next';
import Card from '@/components/Card';
import { FieldValues } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { ReminderLineSelect } from './components/ReminderLineSelect';
import { getConditionOptions, getTriggerOptions } from './utils/setting.utils';
import { Button } from '@/components/Button';
import { useGetEmailTemplatesSelectOptions } from '@/hooks/email-template.hooks';
import Loader from '@/components/Loading/Loader';
import { EmailTemplateType } from '@/api/entities/email-template';
import { Accordion } from '@/components/Accordion';
import { twMerge } from 'tailwind-merge';
import {
  useCreateEmailReminder,
  useDeleteEmailReminder,
  useListAllEmailReminders,
  useUpdateEmailReminder,
} from '@/hooks/email-reminder.hooks';
import {
  EmailReminderSendNow,
  EmailReminderTriggerType,
} from '@/api/entities/email-reminder';
import { TEmailReminderSchema } from '@/schemas/email-reminder/email-reminder.schema';
import { WarningMessage } from '@/components/Message/WarningMessage';
import Modal from '@/components/Modal';
import { showModal } from '@/utils/modal.utils';
import { useNavigate } from 'react-router-dom';

interface EmailReminderManagementProps<
  TSchema extends FieldValues,
> extends FormManagementProps<TSchema> {
  globalEmailSettingId?: string;
  groupId?: string;
}

export const EmailReminderManagement = <TSchema extends FieldValues>({
  t,
  globalEmailSettingId,
  groupId,
}: EmailReminderManagementProps<TSchema>) => {
  const navigate = useNavigate();

  const [selectedTrigger, setSelectedTrigger] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedCondition, setSelectedCondition] = useState('');

  const { getEmailTemplatesSelectOptions, isLoadingAllEmailTemplates } =
    useGetEmailTemplatesSelectOptions({
      type: EmailTemplateType.INSPECTION_REQUEST,
    });

  const listQuery = globalEmailSettingId
    ? { globalEmailSettingId }
    : groupId
      ? { groupId }
      : undefined;

  const { data: emailReminders, isLoading: isLoadingReminders } =
    useListAllEmailReminders(listQuery);

  const { mutate: createReminder, isPending: isCreating } =
    useCreateEmailReminder({
      t,
      showDefaultSuccessMessage: true,
      handleSuccess: () => {
        setSelectedTrigger('');
        setSelectedTemplate('');
        setSelectedCondition('');
      },
    });

  const { mutate: deleteReminder } = useDeleteEmailReminder({
    t,
    showDefaultSuccessMessage: true,
  });

  const { mutate: updateReminder, isPending: isUpdating } =
    useUpdateEmailReminder({
      t,
      showDefaultSuccessMessage: true,
      handleSuccess: () => {
        setEditedReminders({});
      },
    });

  const [editedReminders, setEditedReminders] = useState<
    Record<string, Partial<TEmailReminderSchema>>
  >({});

  const triggerOptions = getTriggerOptions(t);
  const conditionOptions = getConditionOptions(t);
  const emailTemplateOptions: { label: string; value: string }[] =
    getEmailTemplatesSelectOptions() ?? [];

  const isAddDisabled =
    !selectedTrigger || !selectedTemplate || !selectedCondition || isCreating;

  const isNowTriggerValue = (value?: string | null) =>
    value === EmailReminderSendNow.NOW;

  const reminderTriggerValue = (reminder: TEmailReminderSchema) =>
    reminder.sendNow
      ? EmailReminderSendNow.NOW
      : reminder.triggerDay != null
        ? `${reminder.triggerDay}`
        : '';

  const isDuplicate = (
    triggerDay: number | null,
    sendNow: boolean,
    emailTemplateId: string,
    triggerType: string,
    excludeId?: string,
  ) => {
    return emailReminders?.data?.some(
      (r: TEmailReminderSchema) =>
        r.id !== excludeId &&
        r.emailTemplateId === emailTemplateId &&
        r.triggerType === triggerType &&
        (sendNow ? !!r.sendNow : !r.sendNow && r.triggerDay === triggerDay),
    );
  };

  const handleAdd = () => {
    if (isAddDisabled) return;

    const sendNow = isNowTriggerValue(selectedTrigger);
    const triggerDay = sendNow ? null : Number(selectedTrigger);

    if (
      isDuplicate(triggerDay, sendNow, selectedTemplate, selectedCondition)
    ) {
      showModal('#validation-modal');
      return;
    }

    const newReminder: TEmailReminderSchema = {
      triggerDay,
      sendNow,
      emailTemplateId: selectedTemplate,
      triggerType: selectedCondition as EmailReminderTriggerType,
      ...(globalEmailSettingId && { globalEmailSettingId }),
      ...(groupId && { groupId }),
    };

    createReminder(newReminder);
  };

  const getTriggerLabel = (reminder: TEmailReminderSchema) => {
    const value = reminderTriggerValue(reminder);
    const option = triggerOptions.find((o) => o.value === value);
    return option?.label ?? value;
  };

  const getTemplateLabel = (emailTemplateId?: string | null) => {
    const option = emailTemplateOptions.find(
      (o) => o.value === emailTemplateId,
    );
    return option?.label;
  };

  const getConditionLabel = (triggerType?: EmailReminderTriggerType | null) => {
    const option = conditionOptions.find((o) => o.value === triggerType);
    return option?.label ?? triggerType;
  };

  const handleEditField = (
    id: string,
    field: keyof TEmailReminderSchema,
    value: string,
  ) => {
    setEditedReminders((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleUpdate = (reminder: TEmailReminderSchema) => {
    const edits = editedReminders[reminder.id!];
    if (!edits) return;

    const triggerEdited = 'triggerDay' in edits;
    const editedTriggerStr = triggerEdited
      ? (edits.triggerDay as unknown as string)
      : undefined;

    const sendNow = triggerEdited
      ? isNowTriggerValue(editedTriggerStr)
      : !!reminder.sendNow;

    const triggerDay = sendNow
      ? null
      : triggerEdited
        ? Number(editedTriggerStr)
        : (reminder.triggerDay ?? null);

    const emailTemplateId =
      (edits.emailTemplateId as string) ?? reminder.emailTemplateId;
    const triggerType = (edits.triggerType as string) ?? reminder.triggerType;

    if (
      isDuplicate(
        triggerDay,
        sendNow,
        emailTemplateId!,
        triggerType!,
        reminder.id,
      )
    ) {
      showModal('#validation-modal');
      return;
    }

    updateReminder({
      ...reminder,
      ...edits,
      triggerDay,
      sendNow,
      ...(globalEmailSettingId && { globalEmailSettingId }),
      ...(groupId && { groupId }),
    });
  };

  const renderEmailReminders = () => {
    if (!emailReminders?.data?.length) return null;

    return emailReminders.data.map((reminder: TEmailReminderSchema) => {
      const edits = editedReminders[reminder.id!] ?? {};
      const reminderTrigger = reminderTriggerValue(reminder);
      const currentTrigger =
        'triggerDay' in edits
          ? String(edits.triggerDay)
          : reminderTrigger;
      const currentTemplate = edits.emailTemplateId ?? reminder.emailTemplateId;
      const currentCondition = edits.triggerType ?? reminder.triggerType;
      const allFilled =
        !!currentTrigger && !!currentTemplate && !!currentCondition;
      const hasChanges =
        allFilled &&
        (currentTrigger !== reminderTrigger ||
          currentTemplate !== reminder.emailTemplateId ||
          currentCondition !== reminder.triggerType);

      return (
        <Accordion
          key={reminder.id}
          triggerWrapperClassName="px-4 py-2 bg-slate-100 border border-2 border-purple-5433FF/50 rounded-lg"
          icon={
            <i
              className={twMerge(
                'ki-filled ki-up-square text-[var(--purple-custom)] text-2xl',
              )}
            />
          }
          titleClassName="mt-0.75 text-slate-700 font-medium text-2sm line-clamp-1"
          deleteIconClassName="font-black !text-red-FF2929"
          bodyContainerClassName="-mt-[8px] border-t border-purple-5433FF/25 border-r border-r-2 border-b border-b-2 border-l border-l-2 border-purple-5433FF/50 bg-slate-100 rounded-bl-lg rounded-br-lg"
          title={
            <div className="flex items-center gap-[8px]">
              <div className="w-[30px] text-[13.5px]">
                {getTriggerLabel(reminder)}
              </div>
              <div className="w-[300px] flex items-center">
                <div className="text-black/30 text-[14.5px] font-medium mr-1">
                  -
                </div>
                <span className="text-[13.5px] text-gray-600 font-medium">
                  {t('common:template')}&nbsp;:&nbsp;
                </span>
                &nbsp;
                <span
                  className={twMerge(
                    'text-purple-6A00F4 text-[13.5px] line-clamp-1',
                    !getTemplateLabel(reminder.emailTemplateId) &&
                      'text-red-FF2929',
                  )}
                >
                  {getTemplateLabel(reminder.emailTemplateId) ||
                    t('common:inactiveTemplate')}
                </span>
                <i
                  // className="ml-1.5 relative top-[1.5px] ki-outline ki-notepad-edit text-blue-2D00F7 font-semibold !text-[15px] z-10"
                  className="ml-1.5 relative -top-[1px] ki-outline ki-notepad-edit text-black font-semibold !text-[15px] z-10"
                  onClick={() => {
                    navigate(
                      `/email-template/edit/${reminder.emailTemplateId}`,
                    );
                  }}
                />
              </div>
              <div className="text-black/25 text-[14.5px] font-thin">|</div>
              &nbsp;
              <div>
                <span className="text-[13.5px] text-gray-600 font-medium">
                  {t('common:condition')}&nbsp;:&nbsp;
                </span>
                &nbsp;
                <span className="text-purple-6A00F4 text-[13.5px]">
                  {getConditionLabel(reminder.triggerType)}
                </span>
              </div>
            </div>
          }
          onDelete={() => {
            if (reminder.id) {
              deleteReminder(reminder.id);
            }
          }}
        >
          <div className="mt-2 flex items-end gap-3">
            <ReminderLineSelect
              label={t('common:trigger')}
              placeholder={t('common:chooseTrigger')}
              options={triggerOptions}
              value={currentTrigger}
              onChange={(e) =>
                handleEditField(reminder.id!, 'triggerDay', e.target.value)
              }
            />
            <ReminderLineSelect
              label={t('common:emailTemplate')}
              placeholder={t('common:chooseEmailTemplate')}
              options={emailTemplateOptions}
              value={
                (edits.emailTemplateId as string) ??
                reminder.emailTemplateId ??
                ''
              }
              onChange={(e) =>
                handleEditField(reminder.id!, 'emailTemplateId', e.target.value)
              }
            />
            <ReminderLineSelect
              label={t('common:condition')}
              placeholder={t('common:chooseCondition')}
              options={conditionOptions}
              value={
                (edits.triggerType as string) ?? reminder.triggerType ?? ''
              }
              onChange={(e) =>
                handleEditField(reminder.id!, 'triggerType', e.target.value)
              }
            />
            <Button
              type="button"
              text={t('common:validate')}
              buttonClassName="h-[35px] gap-1.5 disabled:bg-[--tw-gray-600] px-2.5 text-2sm bg-primary"
              disabled={!hasChanges || isUpdating}
              // leftIcon={
              //   <i className="ki-filled ki-notepad-edit !text-[15.5px]" />
              // }
              onClick={() => handleUpdate(reminder)}
            />
          </div>
        </Accordion>
      );
    });
  };

  return (
    <>
      <div>
        <Card
          title={
            <TitleSection
              defaultIconClassName="-mt-0.75 ki-calendar-tick"
              textClassName="flex flex-1 font-normal"
              text={
                <div className="flex flex-1 items-center gap-5">
                  <div className="flex flex-1 font-semibold">
                    {t('common:automatedEmails')}
                  </div>
                </div>
              }
            />
          }
          bodyClassName="py-8 flex flex-col gap-8"
        >
          <>
            <div className="mt-0.5 flex gap-2">
              <i className="-mt-0.5 ki-outline ki-information text-[21px] text-primary" />
              <div className="text-sm text-gray-600">
                <Trans
                  i18nKey={t('common:automatedEmailsInfos')}
                  components={{ br: <br /> }}
                />
              </div>
            </div>
            <div className="px-3 py-6 bg-purple-EFEFFD/40 rounded-lg flex items-center flex-col gap-8">
              <div className="flex items-end gap-3">
                <ReminderLineSelect
                  label={t('common:trigger')}
                  placeholder={t('common:chooseTrigger')}
                  options={triggerOptions}
                  value={selectedTrigger}
                  onChange={(e) => setSelectedTrigger(e.target.value)}
                />
                <ReminderLineSelect
                  label={t('common:emailTemplate')}
                  placeholder={t('common:chooseEmailTemplate')}
                  options={emailTemplateOptions}
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                />
                <ReminderLineSelect
                  label={t('common:condition')}
                  placeholder={t('common:chooseCondition')}
                  options={conditionOptions}
                  value={selectedCondition}
                  onChange={(e) => setSelectedCondition(e.target.value)}
                />
                <Button
                  type="button"
                  text={t('common:add')}
                  buttonClassName="h-[35px] gap-1.5 disabled:bg-[--tw-gray-600] px-4 text-2sm bg-pink-F20089"
                  disabled={isAddDisabled}
                  leftIcon={
                    <i className="ki-filled ki-plus-circle !text-[17px]" />
                  }
                  onClick={handleAdd}
                />
              </div>
              <div className="w-[684.48px] space-y-3">
                {renderEmailReminders()}
              </div>
            </div>
          </>
        </Card>
      </div>
      <Modal
        id="validation-modal"
        title={t('common:information')}
        centerY
        showCancelBtn={false}
        showSecondBtn={true}
        secondBtnLabel="Ok"
        size="small"
      >
        <WarningMessage
          message={t('common:emailReminderAlreadyExists')}
          containerClassName="max-w-none"
        />
      </Modal>
      {(isLoadingAllEmailTemplates || isLoadingReminders) && <Loader />}
    </>
  );
};
